/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import { configsSelector } from '../../store/reducers/servicesConfigs';
import { loadingPredictionsSelector } from '../../store/reducers/predictions';
import {
  getAgentData,
  loadingAgentsDataSelector,
} from '../../store/reducers/agentsData';
import {
  clearSamplesData,
  deleteSamples,
  getSamplesData,
  updateSamples,
} from '../../store/actions/agentsData';
import {
  clearPredictionsList as clearPredictionsListAction,
  makePrediction,
} from '../../store/actions/predictions';
import { lookupTableSelector } from '../../store/reducers/LookupTable';
import { getLookupTable } from '../../store/actions/lookupTable';
import { flagsSelector } from '../../store/reducers/flags';
import { updateFlags as updateFlagsAction } from '../../store/actions/flags';

const useStyles = makeStyles((theme) => ({
  select: {
    margin: theme.spacing(2),
    minWidth: 300,
  },
  tableContainer: {
    maxHeight: 520,
  },
  redButton: {
    backgroundColor: '#ff0000',
    color: 'white',
    textTransform: 'none',
    fontFamily: 'Exo 2',
  },
  blueButton: {
    backgroundColor: '#2ca0f7',
    color: 'white',
    textTransform: 'none',
    fontFamily: 'Exo 2',
  },
  darkBlueButton: {
    backgroundColor: '#3C78D8',
    color: 'white',
    textTransform: 'none',
    fontFamily: 'Exo 2',
  },
  withSpacing: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
  },
  bold: {
    marginBottom: theme.spacing(2),
    fontWeight: 700,
  },
  labelWithValue: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  number: {
    color: '#a9a9a9',
    marginLeft: theme.spacing(1),
  },
  paper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: '1px solid #a9a9a9',
    borderRadius: 4,
  },
  card: { minHeight: '26vw' },
  media: {
    height: '25vw',
    width: '96%',
    margin: 'auto',
    marginTop: '2%',
    marginLeft: '2%',
  },
}));

export default () => {
  const classes = useStyles();
  const [currentConfig, setCurrentConfig] = useState(0);
  const [predictionsToView, setPredictionsToView] = useState([]);
  const [samplesToRelabel, setSamplesToRelabel] = useState([]);
  const [predictionsToDelete, setPredictionsToDelete] = useState([]);
  const [fromDate, setFromDate] = React.useState(moment('01/01/2020'));
  const [toDate, setToDate] = React.useState(moment());
  const datesAreSet = useRef(false);
  const configs = useSelector(configsSelector);
  const loadingAgentsData = useSelector(loadingAgentsDataSelector);
  const loadingPredictions = useSelector(loadingPredictionsSelector);
  const flags = useSelector(flagsSelector);
  const predictionsList = useSelector(
    (state) =>
      state.predictions.predictions[currentConfig]?.response?.predictions
  );
  const lookupTable = useSelector(lookupTableSelector);
  const config = configs[currentConfig];
  const {
    samplesPerLabel = {},
    totalSamples = 0,
    totalLabels = 0,
    allSamplesData,
    flags: lastFlags,
  } = useSelector((state) => getAgentData(state));
  const rows = Object.entries(samplesPerLabel)
    .map(([label, samples]) => ({
      label,
      samplesSize: samples.length,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const configOptions = configs.map(({ host, port }) => `${host}:${port}`);
  let samplesWithinDates;
  let totalSamplesWithinDates;
  let totalLabelsWithinDates;
  if (fromDate && toDate) {
    samplesWithinDates = Object.values(samplesPerLabel)
      .reduce((acc, arr) => acc.concat(arr), [])
      .filter(
        ({ created_at }) =>
          moment(created_at).isBetween(
            fromDate.startOf('day'),
            toDate.endOf('day')
          ) || ''
      );
    totalSamplesWithinDates = samplesWithinDates && samplesWithinDates.length;
    totalLabelsWithinDates = new Set(
      samplesWithinDates.map(({ label }) => label)
    ).size;
  }

  const dispatch = useDispatch();

  const onConfigChange = ({ target }) => {
    setCurrentConfig(target.value);
  };

  const onDeleteLabelSamples = (label) => {
    const samplesIds = samplesPerLabel[label].map(({ uuid }) => uuid);
    setPredictionsToDelete(samplesIds);
  };

  const onDeleteSamplesWithinDates = () => {
    const samplesIds = samplesWithinDates.map(({ uuid }) => uuid);
    setPredictionsToDelete(samplesIds);
  };

  const onDeleteSamplesByIds = (samplesIds) => {
    setPredictionsToDelete(samplesIds);
  };

  const closeDeleteDialog = () => {
    setPredictionsToDelete([]);
  };

  const removeDeletedPredictionsFromDialog = () => {
    const newPredictionsToView = predictionsToView.filter(
      ({ uuid }) => !predictionsToDelete.includes(uuid)
    );
    setPredictionsToView(newPredictionsToView);
  };

  const onDeleteSamples = () => {
    removeDeletedPredictionsFromDialog();
    dispatch(deleteSamples(config, predictionsToDelete, flags));
    closeDeleteDialog();
  };

  const handleDateChange = (date, from) => {
    if (from) {
      setFromDate(date);
    } else {
      setToDate(date);
    }
  };

  const onViewLabelSamples = (label) => {
    const samples = samplesPerLabel[label];
    setPredictionsToView(samples);
  };

  const onViewSamplesWithinDates = (label, data) => {
    switch (label) {
      case 'family':
      case 'capture_trigger':
      case 'img_source':
        return setPredictionsToView(data);
      default:
        setPredictionsToView(samplesWithinDates);
    }
  };

  const closePredictionsViewDialog = () => {
    setPredictionsToView([]);
  };

  const onMakePredictionByImage = (image) => {
    dispatch(makePrediction(config, currentConfig, { image }));
  };

  const clearPredictionsList = () => {
    dispatch(clearPredictionsListAction(currentConfig));
  };

  const closeSamplesRelabelDialog = () => {
    setSamplesToRelabel([]);
  };

  const onUpdateSamples = (newLabel) => {
    // closePredictionsViewDialog();
    // push to next image if exsits

    dispatch(updateSamples(config, samplesToRelabel, newLabel, flags));
    closeSamplesRelabelDialog();
  };

  const onRelabelSample = (uuid) => {
    const sample = Object.values(samplesPerLabel)
      .reduce((acc, arr) => acc.concat(arr), [])
      .filter((s) => s.uuid === uuid);
    setSamplesToRelabel(sample);
  };

  const onRelabelSamples = (label) => {
    const samples = samplesPerLabel[label];
    setSamplesToRelabel(samples);
  };

  const onRelabelSamplesWithinDates = () => {
    setSamplesToRelabel(samplesWithinDates);
  };

  const updateFlags = (_flags) => dispatch(updateFlagsAction(_flags));

  useEffect(() => {
    dispatch(getLookupTable(config));
  }, [config]);

  useEffect(() => {
    if (lastFlags?.local !== flags?.local) {
      dispatch(clearSamplesData());
    }
    dispatch(getSamplesData(config, {}, flags));
  }, [config, flags]);

  useEffect(() => {
    if (!datesAreSet.current && Object.keys(samplesWithinDates).length) {
      datesAreSet.current = true;
      const moments = samplesWithinDates.map(({ created_at }) =>
        moment(created_at)
      );
      const minDate = moment.min(moments);
      setFromDate(minDate);
      const maxDate = moment.max(moments);
      setToDate(maxDate);
    }
  }, [samplesWithinDates]);

  return {
    classes,
    loadingAgentsData,
    loadingPredictions,
    configOptions,
    currentConfig,
    samplesPerLabel,
    totalSamples,
    totalLabels,
    totalSamplesWithinDates,
    totalLabelsWithinDates,
    rows,
    fromDate,
    toDate,
    predictionsToView,
    predictionsList,
    predictionsToDeleteSize: predictionsToDelete.length,
    samplesToRelabel,
    lookupTable,
    allSamplesData,
    flags,
    updateFlags,
    onDeleteSamples,
    closeDeleteDialog,
    onViewLabelSamples,
    onRelabelSample,
    onRelabelSamples,
    onRelabelSamplesWithinDates,
    onUpdateSamples,
    onViewSamplesWithinDates,
    closePredictionsViewDialog,
    onDeleteSamplesWithinDates,
    onDeleteSamplesByIds,
    handleDateChange,
    onDeleteLabelSamples,
    onConfigChange,
    onMakePredictionByImage,
    clearPredictionsList,
    closeSamplesRelabelDialog,
  };
};
